import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiArrowLeft, FiCalendar } = FiIcons;

const ArticleNouvelleGeneration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="pt-52 pb-20 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/ressources"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
            Retour aux ressources
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Vers une nouvelle génération de formateurs
          </h1>
          <div className="flex items-center text-gray-500 text-sm">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>Mis en ligne le 11/03/2026</span>
          </div>
        </motion.div>

        {/* Article content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Pendant longtemps, le métier de formateur d'adultes a vécu dans une forme de clair-obscur. Indispensable à l'économie des compétences, omniprésent dans les entreprises, les organismes de formation, les politiques publiques d'emploi ou d'insertion, il restait pourtant difficile à définir précisément. On devenait formateur "par le terrain", par expérience, par vocation, par reconversion.
            </p>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un métier à contours variables
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  L'activité se construisait souvent au fil des missions, des opportunités et des réseaux. Ce caractère artisanal n'était pas forcément un défaut : il était aussi le signe d'un métier profondément lié aux réalités, aux parcours de vie, à la capacité d'adaptation. Mais cette souplesse avait un prix : une hétérogénéité très forte des pratiques, des niveaux de compétence inégaux, et une reconnaissance institutionnelle fluctuante.
                </p>
                <p>
                  La création et l'inscription au Répertoire national des certifications professionnelles (RNCP) du titre RNCP37275 — "Formateur professionnel d'adultes" — s'inscrivent dans ce mouvement de clarification. Elle marque un basculement : celui d'un métier longtemps informel vers une profession plus lisible, outillée, et désormais structurée autour d'un référentiel national.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un flou devenu intenable
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Dans les faits, on y trouvait plusieurs profils : d'anciens techniciens devenus transmetteurs, des cadres RH animant des formations internes, des consultants, des intervenants ponctuels, des enseignants qui basculent vers l'adulte, des "sachants" dans des domaines variés, et même des experts métiers qui se découvrent une appétence pour la transmission.
                </p>
                <p>
                  Ce flou a aussi créé une forme d'injustice : certains formateurs expérimentés, très compétents, n'avaient aucune reconnaissance officielle. À l'inverse, des intervenants peu formés à la pédagogie pouvaient accéder au marché simplement par opportunité ou réseau. Dans un univers de plus en plus régulé — financeurs, appels d'offres, contrôles, traçabilité — cette situation devenait intenable. Le secteur avait besoin d'un langage commun, d'une "grammaire" professionnelle.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Pourquoi le RNCP37275 arrive maintenant
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Le RNCP37275 arrive dans un moment où plusieurs forces convergent. D'abord, le marché du travail a changé de nature. Les carrières linéaires se raréfient ; la reconversion n'est plus une exception mais une réalité de masse. La formation n'est plus un "bonus" : elle devient un outil de survie économique et sociale.
                </p>
                <p>
                  Ensuite, les exigences de qualité se sont renforcées. La certification Qualiopi, l'encadrement des financements publics, les contrôles de conformité, ont poussé les organismes à se professionnaliser. Et quand les organismes se professionnalisent, ils ont besoin de professionnels en face.
                </p>
                <p>
                  Enfin, le rôle même du formateur a changé. Il ne s'agit plus seulement de "transmettre un contenu". Un formateur d'adultes doit aujourd'hui être à la fois pédagogue, animateur, accompagnateur, évaluateur, médiateur, et parfois même coach de la motivation. Autrement dit : le métier s'est complexifié. Un référentiel devient une nécessité.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Un socle professionnel rendu visible
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  L'intérêt du RNCP37275 est de rendre visible un socle professionnel. Non pas pour rigidifier le métier, mais pour en clarifier les exigences. Il fixe des compétences attendues : concevoir une action de formation, la préparer, l'animer, accompagner les apprenants, évaluer les acquis, inscrire son action dans un cadre professionnel et réglementaire.
                </p>
                <p>
                  Le titre RNCP a aussi une fonction de protection. Il protège les apprenants contre des pratiques approximatives. Il protège les organismes contre des prestations mal structurées. Et il protège les formateurs en leur donnant une légitimité reconnue, notamment lorsqu'ils souhaitent travailler avec des financeurs, répondre à des marchés publics, ou sécuriser leur activité en indépendant.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                La VAE : transformer l'expérience en certification
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  La puissance de ce titre tient aussi à sa compatibilité avec la VAE. Pour un secteur où une large partie des formateurs sont issus de l'expérience, c'est un point crucial. La VAE reconnaît une réalité : on peut être compétent sans avoir suivi un cursus classique.
                </p>
                <p>
                  Mais cette reconnaissance n'est pas automatique : elle exige la capacité à formaliser, à prouver, à structurer ses pratiques. En ce sens, le RNCP37275 incite indirectement à une montée en maturité du métier : il pousse le formateur à expliciter son savoir-faire, à rendre visible sa méthode, à mieux documenter ses actions.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Une nouvelle génération : plus structurée, plus outillée
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Le titre participe à l'émergence d'une nouvelle génération de formateurs, mieux armés pour un secteur devenu exigeant.
                </p>
                <p>
                  <strong>Plus structurés</strong>, d'abord. Ils savent construire un déroulé, définir des objectifs pédagogiques, choisir une méthode adaptée, scénariser une progression. Ils ne "font pas cours" : ils conçoivent une expérience d'apprentissage.
                </p>
                <p>
                  <strong>Plus outillés</strong>, ensuite. Les outils numériques, le blended learning, la classe virtuelle, la scénarisation e-learning, ne sont plus des "options". Ce sont des compétences fondamentales.
                </p>
                <p>
                  <strong>Plus intégrés</strong>, enfin, dans les stratégies de compétences. Le formateur n'est plus un acteur périphérique. Dans l'entreprise, il devient un maillon de la performance. Dans le secteur public, il devient un levier de transformation.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Les risques et les perspectives
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Cette évolution n'est pas sans risques. Toute normalisation comporte un revers : le danger de rigidifier un métier vivant. Le risque serait que la certification devienne une simple "case à cocher", une barrière administrative de plus, sans véritable exigence de qualité.
                </p>
                <p>
                  Reste à observer ce que deviendra ce titre dans la durée. Il devra évoluer. Les innovations pédagogiques, l'IA, l'évaluation adaptative, les micro-certifications, vont bousculer les pratiques. Le référentiel devra intégrer de nouveaux enjeux : accompagner l'autonomie, scénariser l'hybride, évaluer autrement, intégrer les outils d'IA de façon éthique.
                </p>
                <p className="font-semibold text-gray-900 bg-blue-50 p-4 rounded-lg mt-4">
                  Au fond, le RNCP37275 est moins un aboutissement qu'un marqueur : celui d'un secteur qui arrive à maturité et qui cherche à stabiliser ses fondations. Dans une société où apprendre devient une condition de l'employabilité, former des adultes n'est pas un métier "secondaire". C'est un métier d'avenir, un métier de responsabilité, un métier qui touche à la transformation des vies.
                </p>
              </div>
            </div>
          </div>

          {/* Back to resources */}
          <div className="text-center pt-12">
            <Link
              to="/ressources"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
              Retour aux ressources
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArticleNouvelleGeneration;
